// LICENSE_CODE ZON
'use strict'; /*jslint react:true*/
import React, {
    forwardRef,
    useImperativeHandle,
    useState,
    useMemo,
    useEffect,
    useCallback,
} from 'react';
import cn from 'classnames';
import isEqual from 'lodash4/isEqual.js';
import {use_request} from '@pub/hooks/api.js';
import {Button} from '@pub/comp/button';
import {notify_error} from '@pub/util.js';
import {is_required_set, flatten_content, compile_content} from './utils.js';
import {Form_content_ctx, Form_controls_ctx,
    Form_validity_ctx, Form_meta_ctx, Form_disabled_ctx} from './contexts.js';
import './form.less';

function Submit({children, className, disabled, id, processing}){
    return <Button className={cn('sff-submit', className)} type="submit"
      size="m" disabled={disabled} id={id} color_scheme="primary" filled
      loading={processing}>
      {children ?? 'Submit'}
    </Button>;
}

const set_key_if_new = (field, value)=>c=>{
    if (isEqual(c[field], value))
        return c;
    return {...c, [field]: value};
};

const stop_prop = e=>e.stopPropagation();

function Form_inner({
    id,
    children,
    className,
    default_values,
    on_change,
    on_submit,
    on_fail,
    on_success,
    submit_label,
    submit_id,
    submit_cn,
    disabled,
    no_submit,
    footer_content,
}, ref)
{
    const [wrap, etask, {processing}] = use_request();
    const [meta, set_meta] = useState({});
    const [validity, set_validity] = useState({});
    const [content, set_content] = useState(()=>
        flatten_content(default_values));
    const remove_fields = useCallback(fields=>{
        const cleanup = c=>{
            const data = {...c};
            for (const field of fields)
                delete data[field];
            return data;
        };
        set_meta(cleanup);
        set_content(cleanup);
    }, []);
    const drop_data = useCallback(()=>{
        set_meta({});
        set_content({});
    }, []);
    const replace_content = useCallback(data=>
        set_content(flatten_content(data)), []);
    const change_validity = useCallback((field, value)=>
        set_validity(set_key_if_new(field, value)), []);
    const change_value = useCallback((field, value)=>
        set_content(set_key_if_new(field, value)), []);
    const push_meta = useCallback((field, value)=>
        set_meta(set_key_if_new(field, value)), []);
    const controls = useMemo(()=>({
        replace_content,
        change_value,
        change_validity,
        push_meta,
        remove_fields,
        drop_data,
    }), []);
    const handle_submit = useCallback(event=>etask(function*(){
        event.preventDefault();
        try {
            const res = yield wrap(on_submit?.(compile_content(content)));
            if (res?.error)
            {
                notify_error(res.error);
                return void on_fail?.(res.error);
            }
            yield wrap(on_success?.(res));
        } catch(e){
            console.error(e);
        }
    }), [content, on_submit, on_fail, on_success]);
    useEffect(()=>{
        on_change?.(compile_content(content));
    }, [on_change, content]);
    const form_disabled = disabled || processing;
    const has_value = Object.keys(content).some(key=>{
        const v = content[key];
        if (!v)
            return false;
        if (Array.isArray(v))
            return v.length>0;
        if (typeof v == 'object')
            return Object.keys(v).length>0;
        return true;
    });
    const is_invalid = Object.values(validity).some(Boolean);
    const all_required_set = is_required_set(content, meta);
    const submit_disabled = form_disabled||is_invalid
        ||!all_required_set||!has_value;
    useImperativeHandle(ref, ()=>({
        ...controls,
        is_valid: !is_invalid,
        is_required_set: all_required_set,
    }), [controls, is_invalid, all_required_set]);
    return <form className={cn('sdk-form', className)} id={id}
      onSubmit={handle_submit}>
      <Form_controls_ctx.Provider value={controls}>
        <Form_meta_ctx.Provider value={meta}>
          <Form_disabled_ctx.Provider value={form_disabled}>
            <Form_validity_ctx.Provider value={validity}>
              <Form_content_ctx.Provider value={content}>
                <div onClick={stop_prop}>
                  {children}
                </div>
              </Form_content_ctx.Provider>
            </Form_validity_ctx.Provider>
          </Form_disabled_ctx.Provider>
        </Form_meta_ctx.Provider>
      </Form_controls_ctx.Provider>
      {!no_submit&&<div className="sdk-form-footer">
        {footer_content}
        <Submit id={submit_id} disabled={submit_disabled}
          processing={processing} className={submit_cn}>{submit_label}</Submit>
      </div>}
    </form>;
}

export const Form = forwardRef(Form_inner);
